import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PRIMARY = 'bg-blue-600';
const PRIMARY_HOVER = 'bg-blue-700';
const TOGGLE_ON = 'bg-blue-600';
const TOGGLE_OFF = 'bg-gray-300';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    const settings = localStorage.getItem('cookieSettings');
    if (!consent) {
      setShowBanner(true);
    }
    if (settings) {
      setCookieSettings(JSON.parse(settings));
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieSettings', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true
    }));
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'false');
    localStorage.setItem('cookieSettings', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false
    }));
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    setSaved(true);
    setTimeout(() => {
      setShowBanner(false);
      setSaved(false);
    }, 1200);
  };

  const handleSettingsChange = (type: string) => {
    setCookieSettings(prev => ({
      ...prev,
      [type]: !prev[type as keyof typeof prev]
    }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-4 right-4 max-w-xs w-full z-50"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="rounded-2xl shadow-xl bg-white/90 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800 p-4 text-xs sm:text-sm flex flex-col gap-2">
          <p className="text-gray-800 dark:text-gray-200 font-medium mb-1">
            Koristimo kolačiće kako bismo poboljšali vaše iskustvo. Više opcija u podešavanjima.
          </p>
          <div className="flex gap-1 flex-wrap justify-end mb-1">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors border border-gray-300 dark:border-gray-700"
            >
              Podešavanja
            </button>
            <button
              onClick={handleDecline}
              className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors border border-gray-300 dark:border-gray-700"
            >
              Odbij
            </button>
            <button
              onClick={handleAccept}
              className="px-2 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors shadow"
            >
              Prihvati sve
            </button>
          </div>

          {showSettings && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-sm font-bold mb-2 text-gray-900 dark:text-white">Podešavanja kolačića</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white">Neophodni</h4>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Uvek uključeni</p>
                  </div>
                  <div className="relative inline-block w-8 align-middle select-none">
                    <input
                      type="checkbox"
                      checked={cookieSettings.necessary}
                      disabled
                      className="sr-only"
                    />
                    <div className="block bg-blue-200 dark:bg-blue-900 w-8 h-4 rounded-full"></div>
                    <div className="dot absolute left-1 top-0.5 bg-white w-3 h-3 rounded-full border border-blue-400"></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white">Analitički</h4>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Razumevanje korišćenja sajta</p>
                  </div>
                  <button
                    onClick={() => handleSettingsChange('analytics')}
                    className="relative inline-block w-8 h-4 focus:outline-none"
                    aria-pressed={cookieSettings.analytics}
                  >
                    <span
                      className={`block w-8 h-4 rounded-full transition-colors duration-200 ${cookieSettings.analytics ? TOGGLE_ON : TOGGLE_OFF}`}
                    ></span>
                    <span
                      className={`dot absolute left-1 top-0.5 w-3 h-3 rounded-full transition-transform duration-200 bg-white border border-gray-400 ${cookieSettings.analytics ? 'transform translate-x-4 border-blue-600' : ''}`}
                    ></span>
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white">Marketinški</h4>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Personalizovane reklame</p>
                  </div>
                  <button
                    onClick={() => handleSettingsChange('marketing')}
                    className="relative inline-block w-8 h-4 focus:outline-none"
                    aria-pressed={cookieSettings.marketing}
                  >
                    <span
                      className={`block w-8 h-4 rounded-full transition-colors duration-200 ${cookieSettings.marketing ? TOGGLE_ON : TOGGLE_OFF}`}
                    ></span>
                    <span
                      className={`dot absolute left-1 top-0.5 w-3 h-3 rounded-full transition-transform duration-200 bg-white border border-gray-400 ${cookieSettings.marketing ? 'transform translate-x-4 border-blue-600' : ''}`}
                    ></span>
                  </button>
                </div>

                <button
                  onClick={handleSaveSettings}
                  className={`w-full px-2 py-1 text-xs font-semibold text-white ${PRIMARY} hover:${PRIMARY_HOVER} rounded transition-colors mt-2 shadow`}
                  disabled={saved}
                >
                  {saved ? 'Sačuvano!' : 'Sačuvaj podešavanja'}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent; 